// Centralized Czech label + Badge-variant maps for the member/team state
// tracking surfaces (payment, photo, team state). Mirrors seasonPhase.ts
// so the wording and colours never diverge across the teams list, team
// detail, roster rows and the registration screen. See the terminology
// table in AGENTS.md — do not paraphrase these strings.
import type { PaymentStatus } from "./queries/teams";
import type { MembershipKind } from "./queries/memberships";

type BadgeVariant = "ok" | "warn" | "err" | "info" | "muted";

export interface StateBadge {
  label: string;
  variant: BadgeVariant;
}

// --- Team payment (per team, per season) ---

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: "Nezaplaceno",
  paid: "Zaplaceno",
};

export const PAYMENT_STATUS_VARIANT: Record<PaymentStatus, BadgeVariant> = {
  unpaid: "warn",
  paid: "ok",
};

// --- Member photo (per person) ---

export const PHOTO_LABELS: Record<"true" | "false", string> = {
  true: "Má fotku",
  false: "Chybí foto",
};

export const PHOTO_VARIANT: Record<"true" | "false", BadgeVariant> = {
  true: "ok",
  false: "warn",
};

// photoBadge resolves a person's photo state to a label + variant.
export function photoBadge(hasPhoto: boolean): StateBadge {
  const key = hasPhoto ? "true" : "false";
  return { label: PHOTO_LABELS[key], variant: PHOTO_VARIANT[key] };
}

// --- Membership kind (per person, per team, per season) ---

// „Člen" is a real season registration; „Nečlen" is a guest for pohárové
// soutěže. The status is per season, so someone who was a člen last season
// and has not re-registered reads Nečlen now: surfaces showing this outside
// a roster must name the season so it is not read as a permanent attribute.
//
// Two shapes, two jobs. The NOUNS below are what a form select, a filter
// option and a sentence need. In a TABLE the answer is a „Registrovaný" tick
// instead (components/RegisteredTick): a column whose value reads „Člen" on
// nearly every row states the majority, and the tick leaves the gaps to be
// found. The badge survives for the surfaces that are neither: a standalone
// read-only marker outside a table, where a lone glyph would name nothing.
export const MEMBERSHIP_KIND_LABELS: Record<MembershipKind, string> = {
  registered: "Člen",
  guest: "Nečlen",
  foreign: "Zahraniční člen",
};

export const MEMBERSHIP_KIND_VARIANT: Record<MembershipKind, BadgeVariant> = {
  registered: "ok",
  guest: "muted",
  // Not „ok": the tick and the green chip say „registered in ČNSMS", which
  // a member of another federation is not.
  foreign: "info",
};

// memberBadge resolves a membership kind to a label + variant.
export function memberBadge(kind: MembershipKind): StateBadge {
  return { label: MEMBERSHIP_KIND_LABELS[kind], variant: MEMBERSHIP_KIND_VARIANT[kind] };
}

// --- Condensed team state (per team, active season) ---

// TEAM_ISSUE_LABELS maps the backend's condensed state keys
// (teamDTO.state_issues) to Czech badge copy.
export const TEAM_ISSUE_LABELS: Record<string, string> = {
  not_registered: "Chybí registrace",
  // Not an issue at all, and that is the point of having it: a club with
  // no registration this season, and nothing saying it owes one. It rides
  // in the same list because it answers the same question the others do.
  cup_only: "Jen pohárovky",
  unpaid: "Nezaplaceno",
  missing_photo: "Chybí foto",
};

// Not all issues weigh the same. „Chybí registrace" is a debt — the club
// has been a member before and the window is open, so it can and should
// act — and that is an error. An unpaid fee and a missing photo are things
// to chase on a team that otherwise exists, which is what `warn` says.
// „Jen pohárovky" is neither: it states where the club stands.
export const TEAM_ISSUE_VARIANT: Record<string, BadgeVariant> = {
  not_registered: "err",
  cup_only: "muted",
  unpaid: "warn",
  missing_photo: "warn",
};

/**
 * „This team has nobody registered in the season", whichever of the two
 * keys says it: `not_registered` when the club owes a registration,
 * `cup_only` when nothing says it should have one. Surfaces that gate on
 * having a soupiska — the payment toggle, the dashboard's MČR entry
 * button — ask this rather than one key, or a cup-only club reads as
 * registered and is offered what it cannot do.
 */
export function hasNoSeasonRegistration(issues: string[] = []): boolean {
  return issues.includes("not_registered") || issues.includes("cup_only");
}

// Positive state shown when an active team has no outstanding issues.
export const TEAM_READY_LABEL = "Připraveno";

// teamStateBadges condenses a team's outstanding issues into a set of
// badges, only surfacing what is missing. An active team with no issues
// gets a single "Připraveno" badge. Non-active teams should not call this
// (the caller gates on status).
export function teamStateBadges(issues: string[] = []): StateBadge[] {
  if (issues.length === 0) {
    return [{ label: TEAM_READY_LABEL, variant: "ok" }];
  }
  return issues.map((key) => ({
    label: TEAM_ISSUE_LABELS[key] ?? key,
    // An unknown key is a backend issue this frontend has not been taught
    // yet; warn rather than err, since its weight is exactly what is unknown.
    variant: TEAM_ISSUE_VARIANT[key] ?? ("warn" as BadgeVariant),
  }));
}

/**
 * Kterými dveřmi klub přišel, když se sám registroval (Team.applied_flow).
 *
 * Ukazuje se JEN u týmu, který čeká na schválení, a jen tam dává smysl: říká,
 * o co klub požádal, ne co o něm platí teď. Jakmile je schválený, platí to,
 * co říká soupiska.
 *
 * Bez toho vypadaly obě české přihlášky na obrazovce schvalování úplně
 * stejně: validují se stejně a zapisují stejný tým, takže „chceme být členy
 * ČNSMS" a „chceme jen na pohárovky" se nedalo rozlišit.
 */
export const APPLIED_FLOW_LABELS: Record<string, string> = {
  cmf_member: "Žádost o členství v ČNSMS",
  cup_only: "Jen pohárové soutěže",
  foreign: "Zahraniční tým",
};

export const APPLIED_FLOW_VARIANT: Record<string, BadgeVariant> = {
  // „info", ne „ok": schválením teprve členem stane, tohle je zatím žádost.
  cmf_member: "info",
  cup_only: "muted",
  foreign: "muted",
};

/** Badge pro čekající tým, nebo null, když se o dveřích nic neví. */
export function appliedFlowBadge(flow?: string): StateBadge | null {
  if (!flow || !APPLIED_FLOW_LABELS[flow]) return null;
  return { label: APPLIED_FLOW_LABELS[flow], variant: APPLIED_FLOW_VARIANT[flow] };
}

// --- Season documents (per team, per season) ---

// Whether the club agreed to the season's documents (GDPR, soutěžní řád…)
// at „Registrace do sezóny". „Chybí" is warn, not err: it is something to
// chase on a club that otherwise exists, and the next submit fixes it.
export const DOCUMENT_CONSENT_LABELS = {
  agreed: "Souhlasili s dokumenty",
  pending: "Chybí souhlas s dokumenty",
} as const;

export const DOCUMENT_CONSENT_VARIANT: Record<keyof typeof DOCUMENT_CONSENT_LABELS, BadgeVariant> = {
  agreed: "ok",
  pending: "warn",
};

/** Badge for a club's consents, or null when the season has no documents. */
export function documentConsentBadge(documents: { agreed: boolean }[]): StateBadge | null {
  if (documents.length === 0) return null;
  const key = documents.every((d) => d.agreed) ? "agreed" : "pending";
  return { label: DOCUMENT_CONSENT_LABELS[key], variant: DOCUMENT_CONSENT_VARIANT[key] };
}
